import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import {
  AuditSession,
  ColumnType,
  SourceRow,
} from "../../../../models/audit/auditSession";
import {
  DrySessionSourceRow,
  ProcedureSessionTableShell,
} from "../../../../models/audit/procedureSessionTableShell";
import { useNavigate, useParams } from "react-router-dom";
import { WorkpaperDataSlim } from "../../../../models/audit/workpaperData";
import { useAudit } from "../../../../contexts/auditContext";
import { useHookCache } from "../../../../lib/hookCache";
import {
  Sparkles,
  RefreshCw,
  Info,
  GripVertical,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  BarChart3,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../../../../components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../../../components/ui/hover-card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { toast } from "sonner";
import { ApiError, fetchFastApi } from "../../../../api/fastapi";
import {
  useSessionRunsContext,
  createPlaceholderSessionRun,
  createSessionRun,
  createSessionRunsForUnlockedCells,
  HighlightEdges,
} from "../../../../contexts/sessionRunsContext";
import {
  getLatestAttributeTestResultSummary,
  getLatestCheckResultSummary,
  isUnbuiltAnalyticsColumn,
} from "../../../../lib/sessionRunUtils";
import { resolveCellError } from "../utils/cellError";
import { AnalyticsColumnCell } from "./AnalyticsColumnCell";
import { AnalyticsColumnStatusBanner } from "./AnalyticsNeedsSetupBanner";
import { CheckDecision } from "./CheckDecision";
import { DataCell } from "./DataCell";
import { attributeTestSummaryToCheckResult } from "../../../../models/audit/attributeTestTranslator";
import { useAuth } from "../../../../contexts/authContext";
import { shouldEnableMidshipFeatures } from "../../../../lib/workspaceUtils";
import { useCortanaV2Executor } from "../../../../hooks/useCortanaV2Executor";
import { getAttributeTestColumnLabel } from "../../../../lib/columnNameUtils";
import { columnTypeConfig } from "../../../../lib/columnTypeDisplay";
import { cn } from "../../../../lib/utils";
import { sessionSpecName } from "../../../../lib/sessionSpecName";
import { ControlAttribute } from "../../../../models/audit/control";
import { RegenerateWithInstructionsDialog } from "./RegenerateWithInstructionsDialog";
import { WorkpaperSummaryDialog } from "./WorkpaperSummaryDialog";

// Column resize configuration
const MIN_COLUMN_WIDTH = 120;
const MAX_COLUMN_WIDTH = 600;
const DEFAULT_COLUMN_WIDTH = 200;
const DEFAULT_SAMPLE_WIDTH = 240;
/** Narrowed sample-column width when `compactSampleColumn` is on (fold-out). */
const COMPACT_SAMPLE_WIDTH = 56;
const SAMPLE_COLUMN_ID = "__sample__";

// Active session run states (in-progress). "ready" is intentionally part of
// this rerun-greying set (via `runningSourceRowIds`) — a run still finalizing
// its results should block rerun — while stop eligibility
// (EventRunsPopover's ACTIVE_SESSION_RUN_STATES) deliberately excludes it:
// a run already past execution can no longer be cancelled.
const ACTIVE_STATES = [
  "queued",
  "leased",
  "retry",
  "local_run",
  "ready",
  "dispatched",
];
// Error session run states
const ERROR_STATES = ["failed", "timed_out"];

type SessionRunLike = ReturnType<
  typeof useSessionRunsContext
>["sessionRuns"][number];

/**
 * True when a newer terminal run on the same sample covers every cell `run`
 * covers (row-wide, or a superset of its `targetColumnIds`). Such a run is stale
 * and must not keep cells reading "Running…" (MS-3649: a never-claimed
 * `dispatched` first attempt otherwise pins the spinner until the ~7h sweep).
 */
const isSupersededByNewerTerminalRun = (
  run: SessionRunLike,
  runs: SessionRunLike[],
): boolean => {
  const startedAt = new Date(run.createdAt).getTime();
  return runs.some(
    (newer) =>
      newer.id !== run.id &&
      newer.sourceRowId === run.sourceRowId &&
      !ACTIVE_STATES.includes(newer.state) &&
      new Date(newer.createdAt).getTime() > startedAt &&
      (newer.targetColumnIds === null ||
        (run.targetColumnIds !== null &&
          run.targetColumnIds.every((id) =>
            newer.targetColumnIds?.includes(id),
          ))),
  );
};

// A rerun-with-instructions that persists to the control runs the persist step
// (updating the column spec) BEFORE it dispatches the actual rerun, so for ~10s
// there is no server run for the cell. Without a bridge the "Running" badge
// blinks off during that window and back on when the run dispatches. A pending
// placeholder run keeps the badge lit continuously; it survives the session-runs
// poll's wholesale replace and hands off to the real run once it appears (or
// expires as a safety net so a failed persist can't spin a cell forever).
const RERUN_PENDING_GRACE_MS = 30_000;

const SAMPLE_HAS_NOTHING_TO_RUN =
  "No test in this sample is connected to a published workflow yet, so there is nothing to run. Attach or build one in Optro Analytics and publish it first.";

/** True when tests are actively running or awaiting a backend retry. */
function isTestsStateBusy(state: string): boolean {
  return state === "processing" || state === "retrying";
}

export interface CurrentSelection {
  checkResultId?: string;
  sourceRowId?: string;
  documentId?: string;
  attributeId?: string;
  supportColumnId?: string;
  value?: string;
}

interface WorkPaperTableProps {
  tableShell: ProcedureSessionTableShell;
  title?: string;
  onCellSelection: ({
    checkId,
    sourceRowId,
    documentId,
    attributeId,
    checkResultId,
    supportColumnId,
    value,
    evalColumnId,
  }: {
    checkId?: string;
    sourceRowId?: string;
    documentId?: string;
    attributeId?: string;
    checkResultId?: string;
    supportColumnId?: string;
    value?: string;
    evalColumnId?: string;
  }) => void;
  onCellEdit?: (
    newValue: string,
    sourceRowId: string,
    supportColumnId: string,
  ) => Promise<void>;
  currentSelection?: CurrentSelection;
  workpaperData: WorkpaperDataSlim | null;
  showNavigation?: boolean;
  currentTableIndex?: number;
  totalTables?: number;
  onNavigateToTable?: (index: number) => void;
  attributes: ControlAttribute[];
  /** Narrows the sample column to just the sample number — used when the table
   * is the grid pane of the inline fold-out, to claw back width. */
  compactSampleColumn?: boolean;
  /** Parent-held horizontal scroll offset. The fold-out remounts this table on
   * every cell click, which resets a fresh scroll container to the leftmost
   * column; the parent holds the offset in a ref so the table can restore it on
   * mount and a click never moves the grid. */
  persistedScrollLeftRef?: React.MutableRefObject<number>;
}

/**
 * Workpaper-lazy-load per-row cell renderer for evaluated columns. Reads
 * from `drySourceRow.checkResultsSummary` (shipped by the BE on both
 * `/source-rows` and `/table-shell` envelopes) so the previous per-row
 * `useSourceRow` fanout is gone. Heavy fields (`reason`, `citations`) are
 * only needed by `CheckAnalysisV2` (the eval-column side panel), which
 * continues to fetch them lazily via `useSourceRow` on click.
 */
export type EvalColumnCellProps = {
  procedureSessionId: string;
  drySourceRow: DrySessionSourceRow;
  column: AuditSession["procedure"]["columns"][number];
  isHighlighted: boolean;
  highlightEdges: HighlightEdges | undefined;
  isLast: boolean;
  width: number;
  activeSessionRuns: ReturnType<typeof useSessionRunsContext>["sessionRuns"];
  errorRuns: ReturnType<typeof useSessionRunsContext>["sessionRuns"];
  successfulRuns: ReturnType<typeof useSessionRunsContext>["sessionRuns"];
  currentSelection: CurrentSelection | undefined;
  onCellSelection: WorkPaperTableProps["onCellSelection"];
  // MS-2533: grid lock toggle — locking stamps the result to the current
  // column version (re-affirmed against the current definition).
  onToggleLock?: (
    sourceRowId: string,
    resultId: string,
    currentLockedBy: string | null,
    currentColumnVersion?: number,
  ) => void;
  onRerunPending?: (
    sourceRowId: string,
    targetColumnIds: string[] | null,
  ) => () => void;
};

const EvalColumnCell = ({
  procedureSessionId,
  drySourceRow,
  column,
  isHighlighted,
  highlightEdges,
  isLast,
  width,
  activeSessionRuns,
  errorRuns,
  successfulRuns,
  currentSelection,
  onCellSelection,
  onToggleLock,
  onRerunPending,
}: EvalColumnCellProps) => {
  const cortanaV2 = useCortanaV2Executor();
  // The check result comes from the slim summary on the dry source-row
  // envelope so rendering a cell never triggers a per-row fetch of the
  // heavy result. `reason` and `citations` are therefore stubbed empty in
  // the adapter below — their real values belong to `CheckAnalysisV2`'s
  // on-click data path. `CheckDecision` must never read either field off
  // this value: it would render a badge from data that was never fetched
  // here, and neither the compiler nor the runtime would flag it.
  const checkResultSummary = useMemo(() => {
    if (!column.evaluatedColumn) return undefined;
    return getLatestCheckResultSummary(
      drySourceRow.checkResultsSummary,
      column.evaluatedColumn.id,
    );
  }, [drySourceRow.checkResultsSummary, column.evaluatedColumn]);

  const checkResult = useMemo(() => {
    if (!checkResultSummary) return undefined;
    return {
      id: checkResultSummary.id,
      checkId: checkResultSummary.checkId,
      evalColumnId: checkResultSummary.evalColumnId,
      reviewerType: checkResultSummary.reviewerType,
      userId: checkResultSummary.userId ?? null,
      decision: checkResultSummary.decision,
      reason: "",
      reviewOrder: checkResultSummary.reviewOrder,
      lockedBy: checkResultSummary.lockedBy ?? null,
      issues: checkResultSummary.issues,
      citations: [],
      columnVersion: checkResultSummary.columnVersion,
      runId: checkResultSummary.runId ?? null,
      createdAt: checkResultSummary.createdAt,
      updatedAt: checkResultSummary.updatedAt,
    };
  }, [checkResultSummary]);

  const isLoading =
    !checkResult?.lockedBy &&
    activeSessionRuns.some(
      (run) =>
        run.sourceRowId === drySourceRow.id &&
        (run.targetColumnIds?.includes(column.id || "") ||
          run.targetColumnIds === null),
    );

  const errorInfo = resolveCellError({
    sourceRowId: drySourceRow.id,
    columnId: column.id,
    resultRunId: checkResult?.runId,
    isLoading,
    errorRuns,
    successfulRuns,
    suppressTimeoutCopy: cortanaV2,
    containedColumnFailure: { report: false },
  });

  return (
    <CheckDecision
      currentSelection={currentSelection}
      onCellSelection={onCellSelection}
      checkResult={checkResult}
      sourceRow={drySourceRow}
      column={column}
      width={width}
      isLast={isLast}
      isLoading={isLoading}
      hasError={errorInfo.hasError}
      isTimeout={errorInfo.isTimeout}
      errorMessage={errorInfo.errorMessage}
      isHighlighted={isHighlighted}
      highlightEdges={highlightEdges}
      auditSessionId={procedureSessionId}
      onToggleLock={
        checkResult && onToggleLock
          ? () =>
              onToggleLock(
                drySourceRow.id,
                checkResult.id,
                checkResult.lockedBy ?? null,
                column.columnVersion,
              )
          : undefined
      }
      onRerunPending={onRerunPending}
    />
  );
};

/**
 * Workpaper-lazy-load per-row cell renderer for attribute-test columns.
 * Reads from `drySourceRow.attributeTestResultsSummary` so the cell can
 * render its `Issues Found` / `No Issues Found` badge without firing a
 * per-row `useSourceRow`. The full attribute-test result (with reperformance
 * steps, evidence summary, etc.) is fetched on click in
 * `AttributeTestAnalysis` via `useAttributeTestResultDetail`.
 */
type AttrTestColumnCellProps = Omit<EvalColumnCellProps, "column"> & {
  column: AuditSession["procedure"]["columns"][number];
};

const AttrTestColumnCell = ({
  procedureSessionId,
  drySourceRow,
  column,
  isHighlighted,
  highlightEdges,
  isLast,
  width,
  activeSessionRuns,
  errorRuns,
  successfulRuns,
  currentSelection,
  onCellSelection,
  onToggleLock,
  onRerunPending,
}: AttrTestColumnCellProps) => {
  const cortanaV2 = useCortanaV2Executor();
  // Kept separate from the translated shape: the translator namespaces its id
  // (`attr-test-id-{summary.id}`) for the shared selection state, but the lock
  // endpoint and the summary-cache flip need the RAW result id.
  const latestSummary = useMemo(() => {
    if (!column.attributeTestColumn) return undefined;
    return getLatestAttributeTestResultSummary(
      drySourceRow.attributeTestResultsSummary,
      column.attributeTestColumn.id,
    );
  }, [drySourceRow.attributeTestResultsSummary, column]);

  const translatedCheckResult = useMemo(
    () =>
      latestSummary
        ? attributeTestSummaryToCheckResult(latestSummary, column)
        : undefined,
    [latestSummary, column],
  );

  const isLoading =
    !translatedCheckResult?.lockedBy &&
    activeSessionRuns.some(
      (run) =>
        run.sourceRowId === drySourceRow.id &&
        (run.targetColumnIds?.includes(column.id || "") ||
          run.targetColumnIds === null),
    );

  const errorInfo = resolveCellError({
    sourceRowId: drySourceRow.id,
    columnId: column.id,
    resultRunId: translatedCheckResult?.runId,
    isLoading,
    errorRuns,
    successfulRuns,
    suppressTimeoutCopy: cortanaV2,
    containedColumnFailure: { report: false },
  });

  return (
    <CheckDecision
      currentSelection={currentSelection}
      onCellSelection={onCellSelection}
      checkResult={translatedCheckResult}
      sourceRow={drySourceRow}
      column={column}
      width={width}
      isLast={isLast}
      isLoading={isLoading}
      hasError={errorInfo.hasError}
      isTimeout={errorInfo.isTimeout}
      errorMessage={errorInfo.errorMessage}
      isHighlighted={isHighlighted}
      highlightEdges={highlightEdges}
      auditSessionId={procedureSessionId}
      columnKind="attribute_test"
      onToggleLock={
        latestSummary && onToggleLock
          ? () =>
              onToggleLock(
                drySourceRow.id,
                latestSummary.id,
                latestSummary.lockedBy ?? null,
                column.columnVersion,
              )
          : undefined
      }
      onRerunPending={onRerunPending}
    />
  );
};

/**
 * The id a column's persisted width is stored under — its kind wrapper's id,
 * not the procedure column's.
 *
 * Single-sourced because three readers must agree on it: the header's grid track
 * list, the resize handle, and the width-defaults seeding loop. A kind missing
 * from the track list puts every column after it under the wrong heading.
 */
function columnWidthKey(
  column: AuditSession["procedure"]["columns"][number],
): string | undefined {
  return (
    column.supportColumn?.id ||
    column.evaluatedColumn?.id ||
    column.attributeTestColumn?.id ||
    column.analyticsColumn?.id
  );
}

type ColumnCriteria = {
  passCriteria: string;
  failCriteria: string | null;
  naCriteria: string | null;
};

/**
 * The pass/fail/N/A criteria behind a column's header popover, or `undefined`
 * for a kind that describes itself some other way.
 *
 * Attribute-test and analytics columns carry the same trio and read the same
 * way, so both are answered here rather than by an arm each. One accessor is
 * what keeps the popover body in step with the trigger's own `hasDescription`
 * test: a kind that satisfies the trigger but matches no arm in the body opens
 * a popover with nothing in it.
 */
function columnCriteria(
  column: AuditSession["procedure"]["columns"][number],
): ColumnCriteria | undefined {
  return column.attributeTestColumn ?? column.analyticsColumn ?? undefined;
}

/**
 * The column kinds the grid body renders a cell for.
 *
 * `satisfies Record<ColumnType, true>` is the tripwire: a kind added to
 * `ColumnType` fails to compile until it is listed here, so one cannot ship
 * without a developer reaching this file. It does not check the dispatch below,
 * which branches on the kind wrapper rather than on `ColumnType` — a kind listed
 * here with no arm there still falls through to `reportUnrenderedColumnKind` and
 * renders an empty cell, which reads as an untested one.
 */
const RENDERED_COLUMN_KINDS = {
  support: true,
  evaluated: true,
  attribute_test: true,
  analytics: true,
} satisfies Record<ColumnType, true>;

const reportedUnrenderedColumnIds = new Set<string>();

/**
 * Report a column that matched no cell arm, and render nothing for it.
 *
 * Reported once per column id rather than once per call: the arm this guards
 * sits inside the per-row column map, so one unrenderable column on a
 * 500-sample procedure would repeat itself 500 times per render pass and bury
 * the console the report exists to use. The set is module-scoped because column
 * ids are unique, and the report is worth making once however many times the
 * grid re-renders or remounts around it. The cost is that the suppression
 * outlives its cause: a column still unrenderable on a later visit is silent
 * until the tab reloads.
 *
 * The two halves of the guard answer different failures, which is why only one
 * of them is fatal. A kind added to `ColumnType` cannot reach this file's list
 * without a developer noticing, though nothing proves it gained a dispatch arm.
 * This half is reached by a column carrying none of the four kind wrappers — a
 * wire-contract break — and by a listed kind the dispatch has no arm for, and
 * there the blast radius decides the response: throwing from a row renderer
 * unmounts the whole workpaper, because the app's only error boundary re-throws
 * anything that is not a chunk-load failure. One blank cell plus a console
 * report is proportionate; losing every other column's results to one malformed
 * column is not.
 */
function reportUnrenderedColumnKind(
  column: AuditSession["procedure"]["columns"][number],
): null {
  if (!reportedUnrenderedColumnIds.has(column.id)) {
    reportedUnrenderedColumnIds.add(column.id);
    console.error(
      `Procedure column ${column.id} (columnType ${column.columnType}) matched none of ` +
        `the rendered kinds: ${Object.keys(RENDERED_COLUMN_KINDS).join(", ")}`,
    );
  }
  return null;
}

export const WorkPaperTableV2 = ({
  tableShell,
  title,
  onCellSelection,
  onCellEdit,
  currentSelection,
  workpaperData,
  showNavigation = false,
  currentTableIndex = 0,
  totalTables = 1,
  onNavigateToTable,
  attributes,
  compactSampleColumn = false,
  persistedScrollLeftRef,
}: WorkPaperTableProps) => {
  // Phase 3: identifier used downstream (cache keys, child props,
  // workpaper-data lookups) — sourced from the table-shell envelope.
  const auditSessionId = tableShell.procedureSession.id;
  const navigate = useNavigate();
  const { controlSessionId } = useParams();
  const { controlSession, sessionKind } = useAudit();
  const {
    sessionRuns,
    addSessionRun,
    addSessionRuns,
    fetchSessionRuns,
    highlightedCells,
    clearHighlight,
  } = useSessionRunsContext();
  const { workspace, user } = useAuth();
  const cortanaV2 = useCortanaV2Executor();
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [rowInstructionsDialogOpen, setRowInstructionsDialogOpen] =
    useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [columnInstructionsDialogOpen, setColumnInstructionsDialogOpen] =
    useState(false);
  const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null);
  const [sampleColumnsExpanded, setSampleColumnsExpanded] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const showAttributeTestColumns =
    workspace?.attributeTestColumnsEnabled ||
    shouldEnableMidshipFeatures(workspace?.id, user?.email);

  // Client-only placeholder runs that bridge the persist-to-control window of a
  // rerun-with-instructions (see RERUN_PENDING_GRACE_MS).
  const [pendingReruns, setPendingReruns] = useState<
    { run: ReturnType<typeof createSessionRun>; expiresAt: number }[]
  >([]);

  // Wrap prebuilt runs as poll-proof placeholders. Returns a cleanup that
  // removes them immediately — callers invoke it when the rerun request
  // fails, so a dead placeholder doesn't spin for the full grace window.
  const markRunsPending = useCallback(
    (runs: ReturnType<typeof createSessionRun>[]) => {
      setPendingReruns((prev) => [
        ...prev,
        ...runs.map((run) => ({
          run,
          expiresAt: Date.now() + RERUN_PENDING_GRACE_MS,
        })),
      ]);
      const ids = new Set(runs.map((run) => run.id));
      const remove = () =>
        setPendingReruns((prev) => prev.filter((p) => !ids.has(p.run.id)));
      window.setTimeout(remove, RERUN_PENDING_GRACE_MS);
      return remove;
    },
    [],
  );

  const markRerunPending = useCallback(
    (sourceRowId: string, targetColumnIds: string[] | null) => {
      const placeholder = createPlaceholderSessionRun(
        auditSessionId,
        sourceRowId,
        tableShell.procedure.columns,
        targetColumnIds,
      );
      return placeholder ? markRunsPending([placeholder]) : () => undefined;
    },
    [auditSessionId, tableShell.procedure.columns, markRunsPending],
  );

  // Hand a pending placeholder off to its real run once the server reports one
  // for the same cell(s), so we don't double-count once the rerun dispatches.
  useEffect(() => {
    setPendingReruns((prev) => {
      const next = prev.filter(
        (p) =>
          !sessionRuns.some(
            (run) =>
              ACTIVE_STATES.includes(run.state) &&
              run.id !== p.run.id &&
              run.sourceRowId === p.run.sourceRowId &&
              (p.run.targetColumnIds === null ||
                run.targetColumnIds === null ||
                p.run.targetColumnIds.some((c) =>
                  run.targetColumnIds?.includes(c),
                )),
          ),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [sessionRuns]);

  // Active runs minus any a newer terminal run has superseded, plus live pending
  // placeholders (kept as-is: they bridge the gap before their server row appears).
  const activeSessionRuns = useMemo(
    () => [
      ...sessionRuns.filter(
        (run) =>
          ACTIVE_STATES.includes(run.state) &&
          !isSupersededByNewerTerminalRun(run, sessionRuns),
      ),
      ...pendingReruns
        .filter((p) => p.expiresAt > Date.now())
        .map((p) => p.run),
    ],
    [sessionRuns, pendingReruns],
  );

  // Sample rows with at least one active run, regardless of which column(s)
  // it targets. Mirrors the per-column `isColumnRunning` check below, scoped
  // to the whole row instead of one column, so the sample-level rerun
  // affordances grey out for exactly the same "already running" condition.
  const runningSourceRowIds = useMemo(
    () => new Set(activeSessionRuns.map((run) => run.sourceRowId)),
    [activeSessionRuns],
  );

  // Filter error runs (failed states)
  const errorRuns = useMemo(
    () => sessionRuns.filter((run) => ERROR_STATES.includes(run.state)),
    [sessionRuns],
  );

  // Filter successful runs
  const successfulRuns = useMemo(
    () => sessionRuns.filter((run) => run.state === "succeeded"),
    [sessionRuns],
  );
  const [dragging, setDragging] = useState<{
    colId: string;
    startX: number;
    startWidth: number;
  } | null>(null);
  // Phase 3: dry source rows from the table-shell — no nested
  // `check_results` / `attribute_test_results` / `match_items`. Per-row
  // heavy hydration is lazy via the inner `WorkPaperTableRowCells`
  // component below.
  const orderedSourceRows = useMemo(() => {
    return [...tableShell.sourceRows].sort(
      (a, b) => (a.sampleNumber || 0) - (b.sampleNumber || 0),
    );
  }, [tableShell.sourceRows]);

  // Get unique attribute keys from source rows for expanded sample columns,
  // sorted by the procedure's attribute order
  const sampleAttributeKeys = useMemo(() => {
    if (!orderedSourceRows.length) return [];
    // Get all unique keys from all source rows' data
    const allKeys = new Set<string>();
    for (const row of orderedSourceRows) {
      for (const key of Object.keys(row.data)) {
        if (
          row.data[key] !== null &&
          row.data[key] !== undefined &&
          row.data[key] !== ""
        ) {
          allKeys.add(key);
        }
      }
    }
    // Sort by the actual attributeOrder value from the procedure
    const lookup = tableShell.procedure?.attributeLookup ?? {};
    return Array.from(allKeys).sort(
      (a, b) =>
        (lookup[a]?.attributeOrder ?? Number.MAX_SAFE_INTEGER) -
        (lookup[b]?.attributeOrder ?? Number.MAX_SAFE_INTEGER),
    );
  }, [orderedSourceRows, tableShell.procedure?.attributeLookup]);
  // Phase 3: writes route through `setEntry` on the appropriate cache key.
  // Per-row mutations target `['procedure-session', psId, 'source-rows', srId]`
  // (the `useSourceRow` value type). `workPaperState` flips target
  // `['procedure-session', psId, 'table-shell']` (the per-procedure table
  // shell value type). Heavy hydrated mirrors are also touched so legacy
  // consumers stay consistent until Phase 4 cleanup.
  const { setEntry, getEntry } = useHookCache();

  // MS-2533: per-cell lock toggles for the grid. Each flips the cache entry
  // the grid renders from optimistically, calls the cell type's lock endpoint,
  // and rolls back on failure. Locking never inserts a new row — the shown
  // value stays put; a locked cell is skipped by reruns.
  const flipSupportCellLock = useCallback(
    (
      sourceRowId: string,
      supportColumnId: string,
      lockedBy: string | null,
      stampColumnVersion?: number,
    ) => {
      setEntry<WorkpaperDataSlim>(
        ["workpaper-data", auditSessionId],
        (prev) => {
          const list = prev.data[sourceRowId];
          if (!list) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              [sourceRowId]: list.map((item) =>
                item.supportColumnId === supportColumnId
                  ? {
                      ...item,
                      lockedBy,
                      ...(stampColumnVersion !== undefined
                        ? { columnVersion: stampColumnVersion }
                        : {}),
                    }
                  : item,
              ),
            },
          };
        },
      );
    },
    [setEntry, auditSessionId],
  );

  const handleToggleSupportCellLock = useCallback(
    async (
      sourceRowId: string,
      supportColumnId: string,
      currentLockedBy: string | null,
      currentColumnVersion?: number,
    ) => {
      const nextLockedBy = currentLockedBy ? null : (user?.id ?? null);
      // Locking re-affirms the value against the current definition — mirror
      // the server-side stamp optimistically so no stale flash on unlock.
      // (A failed lock leaves the stamp until the next refetch; harmless.)
      flipSupportCellLock(
        sourceRowId,
        supportColumnId,
        nextLockedBy,
        nextLockedBy ? currentColumnVersion : undefined,
      );
      try {
        await fetchFastApi({
          method: "PUT",
          path: `/matching/match-session/${auditSessionId}/source-row/${sourceRowId}/support-column/${supportColumnId}/lock`,
          body: { locked_by: nextLockedBy },
        });
      } catch (error) {
        console.error("Error toggling support cell lock:", error);
        toast.error("Failed to toggle cell lock");
        flipSupportCellLock(sourceRowId, supportColumnId, currentLockedBy);
      }
    },
    [auditSessionId, user?.id, flipSupportCellLock],
  );

  const flipSummaryLock = useCallback(
    (
      sourceRowId: string,
      resultId: string,
      kind: "eval" | "attribute_test" | "analytics",
      lockedBy: string | null,
      stampColumnVersion?: number,
    ) => {
      const stamp =
        stampColumnVersion !== undefined
          ? { columnVersion: stampColumnVersion }
          : {};
      setEntry<ProcedureSessionTableShell>(
        ["procedure-session", auditSessionId, "table-shell"],
        (prev) => ({
          ...prev,
          sourceRows: prev.sourceRows.map((row) => {
            if (row.id !== sourceRowId) return row;
            if (kind === "eval") {
              return {
                ...row,
                checkResultsSummary: row.checkResultsSummary.map((s) =>
                  s.id === resultId ? { ...s, lockedBy, ...stamp } : s,
                ),
              };
            }
            if (kind === "analytics") {
              return {
                ...row,
                analyticsResultsSummary: row.analyticsResultsSummary.map((s) =>
                  s.id === resultId ? { ...s, lockedBy, ...stamp } : s,
                ),
              };
            }
            return {
              ...row,
              attributeTestResultsSummary: row.attributeTestResultsSummary.map(
                (s) => (s.id === resultId ? { ...s, lockedBy, ...stamp } : s),
              ),
            };
          }),
        }),
      );
    },
    [setEntry, auditSessionId],
  );

  const handleToggleEvalCellLock = useCallback(
    async (
      sourceRowId: string,
      checkResultId: string,
      currentLockedBy: string | null,
      currentColumnVersion?: number,
    ) => {
      const nextLockedBy = currentLockedBy ? null : (user?.id ?? null);
      flipSummaryLock(
        sourceRowId,
        checkResultId,
        "eval",
        nextLockedBy,
        nextLockedBy ? currentColumnVersion : undefined,
      );
      try {
        await fetchFastApi({
          method: "PUT",
          path: `/matching/match-session/${auditSessionId}/source-row/${sourceRowId}/check-result/${checkResultId}/lock`,
          body: { locked_by: nextLockedBy },
        });
      } catch (error) {
        console.error("Error toggling eval cell lock:", error);
        toast.error("Failed to toggle cell lock");
        flipSummaryLock(sourceRowId, checkResultId, "eval", currentLockedBy);
      }
    },
    [auditSessionId, user?.id, flipSummaryLock],
  );

  const handleToggleAttrTestCellLock = useCallback(
    async (
      sourceRowId: string,
      resultId: string,
      currentLockedBy: string | null,
      currentColumnVersion?: number,
    ) => {
      const nextLockedBy = currentLockedBy ? null : (user?.id ?? null);
      flipSummaryLock(
        sourceRowId,
        resultId,
        "attribute_test",
        nextLockedBy,
        nextLockedBy ? currentColumnVersion : undefined,
      );
      try {
        await fetchFastApi({
          method: "POST",
          path: `/matching/attribute-test-results/${resultId}/lock`,
          body: { user_id: nextLockedBy },
        });
      } catch (error) {
        console.error("Error toggling attribute-test cell lock:", error);
        toast.error("Failed to toggle cell lock");
        flipSummaryLock(
          sourceRowId,
          resultId,
          "attribute_test",
          currentLockedBy,
        );
      }
    },
    [user?.id, flipSummaryLock],
  );

  const handleToggleAnalyticsCellLock = useCallback(
    async (
      sourceRowId: string,
      resultId: string,
      currentLockedBy: string | null,
      currentColumnVersion?: number,
    ) => {
      const nextLockedBy = currentLockedBy ? null : (user?.id ?? null);
      // Locking re-stamps the verdict to the current column version, so a failed
      // POST has to restore the stamp the optimistic write overwrote — otherwise
      // a stale, unlocked verdict keeps reading as current. `currentColumnVersion`
      // is the column's version, not the verdict's, so read the pre-write stamp.
      const priorColumnVersion = getEntry<ProcedureSessionTableShell>([
        "procedure-session",
        auditSessionId,
        "table-shell",
      ])
        ?.data?.sourceRows.find((row) => row.id === sourceRowId)
        ?.analyticsResultsSummary.find((s) => s.id === resultId)?.columnVersion;
      flipSummaryLock(
        sourceRowId,
        resultId,
        "analytics",
        nextLockedBy,
        nextLockedBy ? currentColumnVersion : undefined,
      );
      try {
        await fetchFastApi({
          method: "POST",
          path: `/matching/analytics-results/${resultId}/lock`,
          body: { user_id: nextLockedBy },
        });
      } catch (error) {
        console.error("Error toggling analytics cell lock:", error);
        // The route refuses a lock on a verdict superseded mid-flight and one
        // another auditor holds, so the message it sends is worth surfacing —
        // "failed" alone would leave the auditor re-clicking a cell that is no
        // longer theirs to take.
        toast.error(
          error instanceof ApiError
            ? error.getUserMessage()
            : "Failed to toggle cell lock",
        );
        flipSummaryLock(
          sourceRowId,
          resultId,
          "analytics",
          currentLockedBy,
          priorColumnVersion,
        );
      }
    },
    [user?.id, flipSummaryLock, getEntry, auditSessionId],
  );

  const orderedColumns = useMemo(() => {
    if (!tableShell.procedure?.columns) return [];

    const sorted = [...tableShell.procedure.columns].sort(
      (a, b) => a.columnOrder - b.columnOrder,
    );

    // A gated-off attribute-test column is dropped here rather than skipped at
    // render, because this list is what the header track list, the width array
    // and the body cells all walk: hiding it anywhere downstream would leave a
    // track behind and offset every column to its right.
    if (!showAttributeTestColumns) {
      return sorted.filter((col) => !col.attributeTestColumn);
    }

    return sorted;
  }, [tableShell.procedure?.columns, showAttributeTestColumns]);

  // Resolves a procedure column's own `id` (the unified identifier
  // `targetColumnIds`/`highlightedCells`/each cell's `data-column-id` all
  // key on — NOT `column.supportColumn?.id` etc., which is a different,
  // nested-resource id used only for width persistence elsewhere in this
  // file). Accepts `undefined` so out-of-range neighbor lookups in
  // `getHighlightEdges` below don't need their own guard.
  const getColumnId = useCallback(
    (
      column: AuditSession["procedure"]["columns"][number] | undefined,
    ): string | undefined => column?.id,
    [],
  );

  // Highlight lookup, keyed by row then column, built once per
  // `highlightedCells` change. `isCellHighlighted` runs for every rendered
  // cell plus its four neighbors (via `getHighlightEdges`), so a linear scan
  // of the flat highlight array — thousands of entries for a workpaper-wide
  // run — would be O(rows × cols × highlights) per render. The wildcard
  // ("*", row-wide) entry sits in the row's set like any column id and is
  // checked alongside the concrete one.
  const highlightedColumnsByRow = useMemo(() => {
    const byRow = new Map<string, Set<string>>();
    for (const cell of highlightedCells) {
      const columns = byRow.get(cell.sourceRowId) ?? new Set<string>();
      columns.add(cell.columnId);
      byRow.set(cell.sourceRowId, columns);
    }
    return byRow;
  }, [highlightedCells]);

  // Single source of truth for "is this (row, column) cell highlighted" —
  // the wildcard ("*", row-wide) match resolves to every column in that
  // row. Shared by the render loop's `isHighlighted` and by
  // `getHighlightEdges` below, which reads it for the neighboring cells.
  const isCellHighlighted = useCallback(
    (sourceRowId: string, columnId: string | undefined): boolean => {
      if (!columnId) return false;
      const columns = highlightedColumnsByRow.get(sourceRowId);
      return !!columns && (columns.has("*") || columns.has(columnId));
    },
    [highlightedColumnsByRow],
  );

  // Per-cell border sides for the highlighted-region merge (MS-3158 round
  // 5): a side is only drawn when that neighbor — in RENDERED row/column
  // order, i.e. `orderedSourceRows`/`orderedColumns` — is not itself
  // highlighted, so a contiguous highlighted region renders as one block
  // instead of every cell drawing its own full border. Two vertically
  // adjacent wildcard-highlighted (row-wide) rows fall out of this
  // naturally: `isCellHighlighted` resolves the wildcard for both rows on
  // every column, so the shared top/bottom edge between them is suppressed
  // the same as any other adjacent highlighted pair. The sample column
  // (and its expanded attribute columns) never participates in
  // highlighting, so a highlighted cell in the leftmost data column always
  // draws its own left edge — there's no neighbor on that side to merge
  // with.
  const getHighlightEdges = useCallback(
    (rowIndex: number, colIndex: number): HighlightEdges => {
      const sourceRow = orderedSourceRows[rowIndex];
      const columnId = getColumnId(orderedColumns[colIndex]);
      const aboveRowId = orderedSourceRows[rowIndex - 1]?.id;
      const belowRowId = orderedSourceRows[rowIndex + 1]?.id;
      const leftColumnId = getColumnId(orderedColumns[colIndex - 1]);
      const rightColumnId = getColumnId(orderedColumns[colIndex + 1]);
      return {
        top: !aboveRowId || !isCellHighlighted(aboveRowId, columnId),
        bottom: !belowRowId || !isCellHighlighted(belowRowId, columnId),
        left: !leftColumnId || !isCellHighlighted(sourceRow.id, leftColumnId),
        right:
          !rightColumnId || !isCellHighlighted(sourceRow.id, rightColumnId),
      };
    },
    [orderedSourceRows, orderedColumns, getColumnId, isCellHighlighted],
  );

  // Initialize column widths from localStorage and ensure defaults for current columns
  useEffect(() => {
    if (!tableShell.procedure) return;
    const storageKey = `wpv2_widths:${tableShell.procedure.id}`;
    let next: Record<string, number> = {};
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        next = JSON.parse(saved) || {};
      } catch {
        next = {};
      }
    }
    // Ensure defaults for current columns
    for (const column of tableShell.procedure.columns) {
      const colId = columnWidthKey(column);
      if (colId && next[colId] == null) {
        next[colId] = DEFAULT_COLUMN_WIDTH;
      }
    }
    // Include sample column if applicable
    if (tableShell.procedure.procedureBasis === "sample") {
      if (next[SAMPLE_COLUMN_ID] == null) {
        next[SAMPLE_COLUMN_ID] = DEFAULT_SAMPLE_WIDTH;
      }
    }
    setColumnWidths(next);
  }, [tableShell.procedure]);

  // Persist widths
  useEffect(() => {
    if (!tableShell.procedure) return;
    const storageKey = `wpv2_widths:${tableShell.procedure.id}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths));
    } catch {
      // ignore
    }
  }, [columnWidths, tableShell.procedure]);

  const getColumnWidth = useCallback(
    (colId: string) => {
      if (compactSampleColumn && colId === SAMPLE_COLUMN_ID) {
        return COMPACT_SAMPLE_WIDTH;
      }
      return (
        columnWidths[colId] ??
        (colId === SAMPLE_COLUMN_ID
          ? DEFAULT_SAMPLE_WIDTH
          : DEFAULT_COLUMN_WIDTH)
      );
    },
    [columnWidths, compactSampleColumn],
  );

  const getColumnStyle = (colId: string): React.CSSProperties => {
    const width = getColumnWidth(colId);
    // Allow columns to flex to fill available space while respecting min widths
    return { minWidth: width, flex: `1 0 ${width}px`, boxSizing: "border-box" };
  };

  // Single source of truth for per-column widths. Both the body grid template
  // and the sticky-bottom scrollbar's spacer derive from this, so they can't
  // drift when a new column type or condition is added.
  const columnWidthsPx = useMemo(() => {
    const widths: number[] = [];
    if (tableShell.procedure.procedureBasis === "sample") {
      widths.push(getColumnWidth(SAMPLE_COLUMN_ID));
      if (sampleColumnsExpanded) {
        for (const attrKey of sampleAttributeKeys) {
          widths.push(getColumnWidth(`attr_${attrKey}`));
        }
      }
    }
    for (const column of orderedColumns) {
      const colId = columnWidthKey(column);
      if (colId) {
        widths.push(getColumnWidth(colId));
      }
    }
    return widths;
  }, [
    tableShell.procedure.procedureBasis,
    orderedColumns,
    getColumnWidth,
    sampleColumnsExpanded,
    sampleAttributeKeys,
  ]);

  const gridTemplateColumns = useMemo(
    () => columnWidthsPx.map((w) => `${w}px`).join(" "),
    [columnWidthsPx],
  );

  const totalContentWidth = useMemo(
    () =>
      Math.max(
        800,
        columnWidthsPx.reduce((acc, w) => acc + w, 0),
      ),
    [columnWidthsPx],
  );

  const startResize = (colId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging({
      colId,
      startX: e.clientX,
      startWidth: getColumnWidth(colId),
    });
  };

  const resetWidth = (colId: string) => {
    setColumnWidths((prev) => ({
      ...prev,
      [colId]:
        colId === SAMPLE_COLUMN_ID
          ? DEFAULT_SAMPLE_WIDTH
          : DEFAULT_COLUMN_WIDTH,
    }));
  };

  // Handle dragging
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - dragging.startX;
      let newWidth = dragging.startWidth + delta;
      if (newWidth < MIN_COLUMN_WIDTH) newWidth = MIN_COLUMN_WIDTH;
      if (newWidth > MAX_COLUMN_WIDTH) newWidth = MAX_COLUMN_WIDTH;
      setColumnWidths((prev) => ({ ...prev, [dragging.colId]: newWidth }));
    };
    const onUp = () => setDragging(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("mouseleave", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("mouseleave", onUp);
    };
  }, [dragging]);

  const handleRegenerateSample = async (sourceRowId: string) => {
    // Optimistic run over every column the server will run for this row. None
    // means every column is an unbuilt analytics column, which the server
    // answers with a no-op.
    const sessionRun = createPlaceholderSessionRun(
      auditSessionId,
      sourceRowId,
      tableShell.procedure.columns,
      null,
    );
    if (!sessionRun) {
      toast.info(SAMPLE_HAS_NOTHING_TO_RUN);
      return;
    }

    try {
      // Phase 3 PRIMARY write: per-row cache entry consumed by
      // `useSourceRow`. The heavy `['procedure-session', psId]` legacy
      // entry is no longer touched here — Phase 3 cache invariants are
      // (a) per-row writes go to `['procedure-session', psId, 'source-rows', srId]`
      // and (b) `workPaperState` flips go to the table-shell entry.
      setEntry<SourceRow>(
        ["procedure-session", auditSessionId, "source-rows", sourceRowId],
        (prev) => ({ ...prev, testsState: "processing" }),
      );
      addSessionRun(sessionRun);

      toast.info(`Regenerating data for sample...`);
      await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}/work-paper-data/generate/v2/sample/${sourceRowId}`,
        method: "POST",
      });
    } catch (error) {
      console.error("Error regenerating sample data:", error);
      toast.error("Error regenerating sample data");
      // Per-row rollback only.
      setEntry<SourceRow>(
        ["procedure-session", auditSessionId, "source-rows", sourceRowId],
        (prev) => ({ ...prev, testsState: "ready" }),
      );
    }
  };

  const handleRegenerateSampleWithInstructions = async (
    instructions: string,
    persistToControl: boolean,
  ) => {
    if (!selectedRowId) {
      toast.error("No row selected");
      return;
    }

    const placeholder = createPlaceholderSessionRun(
      auditSessionId,
      selectedRowId,
      tableShell.procedure.columns,
      null,
    );
    // With nothing runnable the rerun is a server-side no-op, but a persist to
    // the spec still happens, so only a plain rerun stops here.
    if (!placeholder && !persistToControl) {
      toast.info(SAMPLE_HAS_NOTHING_TO_RUN);
      return;
    }

    let clearPending: (() => void) | undefined;
    try {
      if (placeholder) {
        setEntry<SourceRow>(
          ["procedure-session", auditSessionId, "source-rows", selectedRowId],
          (prev) => ({ ...prev, testsState: "processing" }),
        );

        // Bridge the persist-to-control window so every runnable cell in this
        // row stays "Running" until the real rerun dispatches.
        clearPending = markRunsPending([placeholder]);

        toast.info("Regenerating sample with instructions...");
      } else {
        toast.info(
          `No test in this sample is connected to a published workflow yet, so only the ${sessionSpecName(sessionKind)} will be updated.`,
        );
      }
      await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}/regenerate-with-instructions/sample/${selectedRowId}`,
        method: "POST",
        body: {
          custom_instructions: instructions,
          // Omit the flag when unchecked: a backend without MS-2514 rejects
          // unknown fields (extra_forbidden), so plain reruns must not carry it.
          ...(persistToControl ? { persist_to_control: true } : {}),
        },
      });
    } catch (error) {
      console.error("Error regenerating sample with instructions:", error);
      toast.error(
        error instanceof ApiError
          ? error.getUserMessage()
          : "Error regenerating sample with instructions",
      );
      if (placeholder) {
        clearPending?.();
        setEntry<SourceRow>(
          ["procedure-session", auditSessionId, "source-rows", selectedRowId],
          (prev) => ({ ...prev, testsState: "ready" }),
        );
      }
    }
  };

  // Phase 3: shared lookup that resolves a hydrated `SourceRow` for the
  // unlocked-cells helper. Each visible row already registered an entry
  // via `useSourceRow`, so `getEntry(...)?.data` returns the heavy row;
  // un-hydrated rows (e.g., off-screen) fall back to `undefined`, which
  // the helper treats as conservatively unlocked.
  const getHeavySourceRow = useCallback(
    (sourceRowId: string) =>
      getEntry<SourceRow>([
        "procedure-session",
        auditSessionId,
        "source-rows",
        sourceRowId,
      ])?.data,
    [getEntry, auditSessionId],
  );

  const handleRerunColumn = async (procedureColumnId: string) => {
    // The server answers an unbuilt analytics column with a no-op, so the
    // column is neither marked as processing nor posted. An empty
    // `sessionRunsToAdd` cannot stand in for this check: a fully locked column
    // produces one too.
    const column = tableShell.procedure.columns.find(
      (candidate) => candidate.id === procedureColumnId,
    );
    if (column && isUnbuiltAnalyticsColumn(column)) {
      toast.info(
        "This test is not connected to a published workflow yet, so there is nothing to run. Attach or build one in Optro Analytics and publish it first.",
      );
      return;
    }

    try {
      // Phase 3: workPaperState flip targets the table-shell entry —
      // the per-procedure Phase 3 primary cache.
      setEntry<ProcedureSessionTableShell>(
        ["procedure-session", auditSessionId, "table-shell"],
        (prev) => ({
          ...prev,
          procedureSession: {
            ...prev.procedureSession,
            workPaperState: "processing",
          },
        }),
      );

      // Phase 3: per-column rerun endpoints are NOT covered by BE-WP-3
      // server-side unlocked-cells computation, so we still filter on
      // the client to avoid orphaned optimistic runs on fully-locked
      // rows (which would otherwise spin `EvalColumnCell.isLoading`
      // forever now that the 7s polling fallback is gone). The helper
      // signature was refactored to accept the table-shell shape plus
      // a closure that resolves heavy `SourceRow` entries lazily from
      // the hook cache.
      const sessionRunsToAdd = createSessionRunsForUnlockedCells({
        procedureSessionId: auditSessionId,
        sourceRows: tableShell.sourceRows,
        columns: tableShell.procedure.columns,
        workpaperData,
        getHeavySourceRow,
        targetColumnIds: [procedureColumnId],
      });
      addSessionRuns(sessionRunsToAdd);

      toast.info("Regenerating column...");
      await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}/work-paper-data/generate/v2`,
        method: "POST",
        body: { target_column_ids: [procedureColumnId] },
      });

      // Safety net: align optimistic runs with the authoritative
      // server-side state once the POST is acknowledged. Mirrors
      // `AuditWorkPaperViewV2.tsx`'s `handleGenerateWorkPaper` (the
      // 7s poll loop that previously masked drift is gone).
      if (controlSessionId) {
        await fetchSessionRuns(controlSessionId);
      }
    } catch (error) {
      console.error("Error regenerating column:", error);
      toast.error("Error regenerating column");
      setEntry<ProcedureSessionTableShell>(
        ["procedure-session", auditSessionId, "table-shell"],
        (prev) => ({
          ...prev,
          procedureSession: {
            ...prev.procedureSession,
            workPaperState: "ready",
          },
        }),
      );
    }
  };

  const handleRerunColumnWithInstructions = async (
    instructions: string,
    persistToControl: boolean,
  ) => {
    if (!selectedColumnId) {
      toast.error("No column selected");
      return;
    }
    let clearPending: (() => void) | undefined;
    try {
      setEntry<ProcedureSessionTableShell>(
        ["procedure-session", auditSessionId, "table-shell"],
        (prev) => ({
          ...prev,
          procedureSession: {
            ...prev.procedureSession,
            workPaperState: "processing",
          },
        }),
      );

      // See comment in `handleRerunColumn`: this endpoint is also
      // outside BE-WP-3's coverage, so client-side unlocked-cells
      // filtering is still required. Unlike the plain rerun, these runs
      // go in as poll-proof placeholders — the fetchSessionRuns safety
      // net below would wipe plain optimistic runs while the real ones
      // don't exist until the background persist dispatches.
      const sessionRunsToAdd = createSessionRunsForUnlockedCells({
        procedureSessionId: auditSessionId,
        sourceRows: tableShell.sourceRows,
        columns: tableShell.procedure.columns,
        workpaperData,
        getHeavySourceRow,
        targetColumnIds: [selectedColumnId],
      });
      clearPending = markRunsPending(sessionRunsToAdd);

      toast.info("Regenerating column with instructions...");
      await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}/regenerate-with-instructions/column/${selectedColumnId}`,
        method: "POST",
        body: {
          custom_instructions: instructions,
          // Omit the flag when unchecked: a backend without MS-2514 rejects
          // unknown fields (extra_forbidden), so plain reruns must not carry it.
          ...(persistToControl ? { persist_to_control: true } : {}),
        },
      });

      // Safety net — see comment in `handleRerunColumn`.
      if (controlSessionId) {
        await fetchSessionRuns(controlSessionId);
      }
    } catch (error) {
      console.error("Error regenerating column with instructions:", error);
      toast.error(
        error instanceof ApiError
          ? error.getUserMessage()
          : "Error regenerating column with instructions",
      );
      clearPending?.();
      setEntry<ProcedureSessionTableShell>(
        ["procedure-session", auditSessionId, "table-shell"],
        (prev) => ({
          ...prev,
          procedureSession: {
            ...prev.procedureSession,
            workPaperState: "ready",
          },
        }),
      );
    }
  };

  const getUpToThreeAttributes = (sourceRow: DrySessionSourceRow) => {
    const attributes = Object.keys(sourceRow.data);
    const selectedAttributes: unknown[] = [];
    for (const attribute of attributes) {
      if (selectedAttributes.length >= 3) {
        break;
      }
      if (sourceRow.data[attribute]) {
        selectedAttributes.push(sourceRow.data[attribute]);
      }
    }
    return selectedAttributes.join(", ");
  };

  const getAllAttributes = (sourceRow: DrySessionSourceRow) => {
    return Object.entries(sourceRow.data)
      .filter(
        ([, value]) => value !== null && value !== undefined && value !== "",
      )
      .map(([key, value]) => {
        // Look up the attribute name from the procedure's attributeLookup
        const displayName =
          tableShell.procedure?.attributeLookup?.[key]?.name || key;
        return { key: displayName, value: String(value) };
      });
  };

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);

  const syncScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const left = e.currentTarget.scrollLeft;
      if (persistedScrollLeftRef) persistedScrollLeftRef.current = left;
      for (const ref of [headerScrollRef, bodyScrollRef, bottomScrollRef]) {
        if (ref.current && ref.current !== e.currentTarget) {
          ref.current.scrollLeft = left;
        }
      }
    },
    [persistedScrollLeftRef],
  );

  // Any click inside the grid body clears the highlight — a single
  // capture-phase listener on the body's scroll container, rather than an
  // onClick on every cell. Capture phase runs before the target's own
  // bubble-phase handlers (e.g. a cell's `stopPropagation()`), so those
  // never suppress it. The popover that sets the highlight lives outside
  // this container, so the click that sets a highlight never also clears it.
  const handleGridClickCapture = useCallback(() => {
    if (highlightedCells.length > 0) clearHighlight();
  }, [highlightedCells.length, clearHighlight]);

  // Restore the parent-held horizontal offset on mount, before paint, so the
  // fold-out's per-cell remount doesn't visibly snap the grid back to the
  // leftmost column.
  useLayoutEffect(() => {
    const left = persistedScrollLeftRef?.current;
    if (!left) return;
    for (const ref of [headerScrollRef, bodyScrollRef, bottomScrollRef]) {
      if (ref.current) ref.current.scrollLeft = left;
    }
    // Mount-only restore; later scrolling is captured via syncScroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="border-x border-b border-stone-300 flex flex-col">
      <div className="sticky top-0 z-20 bg-white border-t border-stone-300">
        {title && (
          <div className="px-4 py-3 border-b border-stone-300">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold">{title}</div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setSummaryOpen(true)}
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1.5 text-stone-500 hover:text-stone-700"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Summary
                </Button>
                {showNavigation && onNavigateToTable && (
                  <>
                    <span className="text-sm text-gray-500">
                      {currentTableIndex + 1} of {totalTables}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-7 w-7"
                        onClick={() => onNavigateToTable(currentTableIndex - 1)}
                        disabled={currentTableIndex === 0}
                        title="Previous workpaper"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-7 w-7"
                        onClick={() => onNavigateToTable(currentTableIndex + 1)}
                        disabled={currentTableIndex === totalTables - 1}
                        title="Next workpaper"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {controlSession && (
          <WorkpaperSummaryDialog
            procedureSessionId={auditSessionId}
            controlSession={controlSession}
            open={summaryOpen}
            onOpenChange={setSummaryOpen}
          />
        )}
        <div
          ref={headerScrollRef}
          onScroll={syncScroll}
          className="overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]"
        >
          <div style={{ minWidth: totalContentWidth }}>
            <div className="bg-gray-50 grid" style={{ gridTemplateColumns }}>
              {tableShell.procedure.procedureBasis == "sample" && (
                <>
                  <div
                    className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900 border-b border-stone-300 select-none"
                    style={getColumnStyle(SAMPLE_COLUMN_ID)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">
                        {compactSampleColumn ? "#" : "Sample"}
                      </span>
                      {!compactSampleColumn && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-5 w-5 shrink-0"
                          onClick={() =>
                            setSampleColumnsExpanded((prev) => !prev)
                          }
                          title={
                            sampleColumnsExpanded
                              ? "Collapse sample columns"
                              : "Expand sample columns"
                          }
                        >
                          {sampleColumnsExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                    <div
                      className="absolute top-0 right-[-10px] h-full w-5 cursor-col-resize z-10 group"
                      onMouseDown={(e) => startResize(SAMPLE_COLUMN_ID, e)}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        resetWidth(SAMPLE_COLUMN_ID);
                      }}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize column"
                    >
                      <div className="pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 h-6 w-6 rounded bg-white border border-stone-300 flex items-center justify-center">
                        <GripVertical className="h-3.5 w-3.5 text-stone-950" />
                      </div>
                    </div>
                  </div>
                  {sampleColumnsExpanded &&
                    sampleAttributeKeys.map((attrKey, attrIdx) => {
                      const attrColId = `attr_${attrKey}`;
                      const isLastAttr =
                        attrIdx === sampleAttributeKeys.length - 1 &&
                        orderedColumns.length === 0;
                      const attrDisplayName =
                        tableShell.procedure?.attributeLookup?.[attrKey]
                          ?.name || attrKey;
                      return (
                        <div
                          key={attrColId}
                          className={`relative bg-amber-50 px-3 py-2 text-sm font-medium text-gray-900 border-l border-b border-stone-300 select-none ${isLastAttr ? "border-r" : ""}`}
                          style={getColumnStyle(attrColId)}
                        >
                          <div className="truncate">{attrDisplayName}</div>
                          <div
                            className={`absolute top-0 ${isLastAttr ? "right-0" : "right-[-10px]"} h-full w-5 cursor-col-resize z-10 group`}
                            onMouseDown={(e) => startResize(attrColId, e)}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              resetWidth(attrColId);
                            }}
                            role="separator"
                            aria-orientation="vertical"
                            aria-label="Resize column"
                          >
                            <div className="pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 h-6 w-6 rounded bg-white border border-stone-300 flex items-center justify-center">
                              <GripVertical className="h-3.5 w-3.5 text-stone-950" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </>
              )}
              {orderedColumns.map((column, idx) => {
                const criteria = columnCriteria(column);
                const hasDescription =
                  column.supportColumn?.description ||
                  column.evaluatedColumn?.instructions ||
                  criteria?.passCriteria;
                const isLastColumn = idx === (orderedColumns.length || 0) - 1;

                const isColumnRunning = activeSessionRuns.some(
                  (run) =>
                    run.targetColumnIds === null ||
                    run.targetColumnIds?.includes(column.id),
                );
                const columnKind: ColumnType = column.supportColumn
                  ? "support"
                  : column.evaluatedColumn
                    ? "evaluated"
                    : column.analyticsColumn
                      ? "analytics"
                      : "attribute_test";
                const columnDisplay = columnTypeConfig[columnKind];
                return (
                  <ContextMenu key={column.id}>
                    <ContextMenuTrigger asChild>
                      <div
                        className={`relative px-3 py-2 text-sm font-medium text-gray-900 border-l border-b border-stone-300 bg-gray-50 select-none ${
                          isLastColumn ? "border-r" : ""
                        }`}
                      >
                        <div className="flex items-start gap-1 break-words">
                          <div
                            className={cn(
                              "flex items-center justify-center rounded-full shrink-0 w-6 h-6",
                              columnDisplay.bgColor,
                            )}
                          >
                            {columnDisplay.icon}
                          </div>
                          <div className="flex items-start justify-between gap-1 flex-1 min-w-0">
                            {/* Titled because the code prefix eats into a 200px line-clamp-2,
                                leaving no other way to read a long name in full. */}
                            <span
                              className="font-medium text-gray-900 line-clamp-2 flex-1 min-w-0"
                              title={getAttributeTestColumnLabel(
                                column,
                                tableShell.procedure?.columns || [],
                                attributes,
                              )}
                            >
                              {getAttributeTestColumnLabel(
                                column,
                                tableShell.procedure?.columns || [],
                                attributes,
                              )}
                            </span>
                            {hasDescription && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 shrink-0 hover:bg-stone-200 mt-0"
                                  >
                                    <Info className="h-4 w-4 text-gray-500" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-80 p-3 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
                                  align="start"
                                >
                                  <div className="space-y-2">
                                    <h4 className="font-medium text-sm">
                                      {getAttributeTestColumnLabel(
                                        column,
                                        tableShell.procedure?.columns || [],
                                        attributes,
                                      )}
                                    </h4>
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                      {column.supportColumn?.description ||
                                        column.evaluatedColumn?.instructions ||
                                        (criteria && (
                                          <>
                                            <strong>Pass Criteria:</strong>{" "}
                                            {criteria.passCriteria}
                                            {criteria.failCriteria && (
                                              <>
                                                {"\n\n"}
                                                <strong>
                                                  Fail Criteria:
                                                </strong>{" "}
                                                {criteria.failCriteria}
                                              </>
                                            )}
                                            {criteria.naCriteria && (
                                              <>
                                                {"\n\n"}
                                                <strong>
                                                  N/A Criteria:
                                                </strong>{" "}
                                                {criteria.naCriteria}
                                              </>
                                            )}
                                          </>
                                        ))}
                                    </p>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                          <div
                            className={`absolute top-0 ${
                              isLastColumn ? "right-0" : "right-[-10px]"
                            } h-full w-5 cursor-col-resize z-10 group`}
                            onMouseDown={(e) => {
                              const colId = columnWidthKey(column);
                              if (colId) {
                                startResize(colId, e);
                              }
                            }}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              const colId = columnWidthKey(column);
                              if (colId) {
                                resetWidth(colId);
                              }
                            }}
                            role="separator"
                            aria-orientation="vertical"
                            aria-label="Resize column"
                          >
                            <div className="pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 h-6 w-6 rounded bg-white border border-stone-300 flex items-center justify-center">
                              <GripVertical className="h-3.5 w-3.5 text-stone-950" />
                            </div>
                          </div>
                        </div>
                        {column.analyticsColumn && (
                          <AnalyticsColumnStatusBanner
                            analyticsColumn={column.analyticsColumn}
                          />
                        )}
                      </div>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="w-52">
                      <ContextMenuLabel>Column Actions</ContextMenuLabel>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        className="cursor-pointer flex items-center gap-2"
                        onClick={() => handleRerunColumn(column.id)}
                        disabled={
                          isColumnRunning ||
                          controlSession?.status === "complete"
                        }
                      >
                        <RefreshCw className="h-4 w-4" />
                        Re-run column
                      </ContextMenuItem>
                      {/* Instructions cannot reach an analytics column: the
                          re-run resolves analytics through the ordinary seam,
                          which takes no instructions, so offering the item
                          would promise guidance the re-run silently drops. */}
                      {!column.analyticsColumn && (
                        <ContextMenuItem
                          className="cursor-pointer flex items-center gap-2"
                          onClick={() => {
                            setSelectedColumnId(column.id);
                            setColumnInstructionsDialogOpen(true);
                          }}
                          disabled={
                            isColumnRunning ||
                            controlSession?.status === "complete"
                          }
                        >
                          <Sparkles className="h-4 w-4" />
                          Re-run column with instructions...
                        </ContextMenuItem>
                      )}
                    </ContextMenuContent>
                  </ContextMenu>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div
        ref={bodyScrollRef}
        className="overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]"
        onScroll={syncScroll}
        onClickCapture={handleGridClickCapture}
      >
        <div style={{ minWidth: totalContentWidth }}>
          <div>
            {orderedSourceRows.map((sourceRow, index) => (
              <div
                key={sourceRow.id}
                className="grid min-h-[75px]"
                style={{ gridTemplateColumns }}
              >
                {tableShell.procedure.procedureBasis == "sample" && (
                  <>
                    <div
                      className="sticky left-0 z-10 bg-white px-3 py-2 border-b border-stone-300"
                      style={{ overflow: "hidden" }}
                    >
                      {compactSampleColumn ? (
                        <div className="text-center text-sm font-medium text-gray-900">
                          {index + 1}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between min-w-0">
                          <HoverCard openDelay={200} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <div
                                className="cursor-pointer hover:underline flex-grow min-w-0"
                                onClick={() => {
                                  const sessionId =
                                    controlSessionId || controlSession?.id;
                                  if (!sessionId) {
                                    toast.error("Control session not found");
                                    return;
                                  }
                                  navigate(
                                    `/audit/control-sessions/session/${sessionId}?auditSessionId=${auditSessionId}&selectedSourceRow=${sourceRow.id}`,
                                  );
                                }}
                              >
                                <div className="text-sm font-medium text-gray-900">
                                  Sample {index + 1}
                                </div>
                                {!sampleColumnsExpanded && (
                                  <div className="text-sm text-gray-500 break-all whitespace-nowrap overflow-hidden text-ellipsis">
                                    {getUpToThreeAttributes(sourceRow)}
                                  </div>
                                )}
                              </div>
                            </HoverCardTrigger>
                            <HoverCardContent
                              className="w-80 p-3 max-h-96 overflow-y-auto"
                              align="start"
                              side="right"
                            >
                              <div className="space-y-2">
                                <h4 className="font-medium text-sm">
                                  Sample {index + 1} - All Columns
                                </h4>
                                <div className="space-y-1.5">
                                  {getAllAttributes(sourceRow).map(
                                    ({ key, value }) => (
                                      <div key={key} className="text-sm">
                                        <span className="font-medium text-gray-700">
                                          {key}:
                                        </span>{" "}
                                        <span className="text-gray-600">
                                          {value}
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                          <DropdownMenu>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 rounded-full shrink-0"
                                      disabled={
                                        controlSession?.status === "complete" ||
                                        isTestsStateBusy(
                                          sourceRow.testsState,
                                        ) ||
                                        runningSourceRowIds.has(sourceRow.id)
                                      }
                                      title={
                                        runningSourceRowIds.has(sourceRow.id)
                                          ? undefined
                                          : "Regenerate sample data"
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <RefreshCw
                                        className={`h-4 w-4 ${
                                          isTestsStateBusy(sourceRow.testsState)
                                            ? "animate-spin"
                                            : ""
                                        }`}
                                      />
                                    </Button>
                                  </DropdownMenuTrigger>
                                </span>
                              </TooltipTrigger>
                              {runningSourceRowIds.has(sourceRow.id) && (
                                <TooltipContent>
                                  Sample is already running
                                </TooltipContent>
                              )}
                            </Tooltip>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                disabled={runningSourceRowIds.has(sourceRow.id)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRegenerateSample(sourceRow.id);
                                }}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Re-run sample
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={runningSourceRowIds.has(sourceRow.id)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedRowId(sourceRow.id);
                                  setRowInstructionsDialogOpen(true);
                                }}
                              >
                                <Sparkles className="h-4 w-4 mr-2" />
                                Re-run with instructions
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                    {sampleColumnsExpanded &&
                      sampleAttributeKeys.map((attrKey) => (
                        <div
                          key={`${sourceRow.id}_attr_${attrKey}`}
                          className="px-3 py-2 border-l border-b border-stone-300 bg-amber-50/30"
                        >
                          <div className="text-sm text-gray-700 break-words">
                            {sourceRow.data[attrKey] !== null &&
                            sourceRow.data[attrKey] !== undefined
                              ? String(sourceRow.data[attrKey])
                              : "-"}
                          </div>
                        </div>
                      ))}
                  </>
                )}
                {orderedColumns.map((column, idx) => {
                  const isLast = idx === (orderedColumns.length || 0) - 1;
                  // `isCellHighlighted` resolves the "*" row-wide wildcard a
                  // targetColumnIds===null run produces (see
                  // sessionRunsContext) against every column in the row.
                  const isHighlighted = isCellHighlighted(
                    sourceRow.id,
                    column.id,
                  );
                  // Only highlighted cells need their border sides computed
                  // — an unhighlighted cell renders no shadow regardless.
                  const highlightEdges = isHighlighted
                    ? getHighlightEdges(index, idx)
                    : undefined;
                  if (column.supportColumn) {
                    const columnData = workpaperData?.data[sourceRow.id]?.find(
                      (data) =>
                        data.sourceRowId === sourceRow.id &&
                        data.supportColumnId === column.supportColumn?.id,
                    );
                    const isLoading =
                      !columnData?.lockedBy &&
                      activeSessionRuns.some(
                        (run) =>
                          run.sourceRowId === sourceRow.id &&
                          (run.targetColumnIds?.includes(column.id) ||
                            run.targetColumnIds === null),
                      );
                    const errorInfo = resolveCellError({
                      sourceRowId: sourceRow.id,
                      columnId: column.id,
                      resultRunId: columnData?.runId,
                      isLoading,
                      errorRuns,
                      successfulRuns,
                      suppressTimeoutCopy: cortanaV2,
                      containedColumnFailure: { report: false },
                    });
                    return (
                      <DataCell
                        key={column.supportColumn.id}
                        column={column}
                        columnData={columnData}
                        sourceRow={sourceRow}
                        onCellSelection={onCellSelection}
                        onCellEdit={onCellEdit}
                        currentSelection={currentSelection}
                        width={getColumnWidth(column.supportColumn.id)}
                        isLast={isLast}
                        isLoading={isLoading}
                        hasError={errorInfo.hasError}
                        isTimeout={errorInfo.isTimeout}
                        errorMessage={errorInfo.errorMessage}
                        isHighlighted={isHighlighted}
                        highlightEdges={highlightEdges}
                        auditSessionId={auditSessionId}
                        onToggleLock={handleToggleSupportCellLock}
                        onRerunPending={markRerunPending}
                      />
                    );
                  } else if (column.evaluatedColumn) {
                    return (
                      <EvalColumnCell
                        key={column.evaluatedColumn.id}
                        procedureSessionId={auditSessionId}
                        drySourceRow={sourceRow}
                        column={column}
                        isHighlighted={isHighlighted}
                        highlightEdges={highlightEdges}
                        isLast={isLast}
                        width={getColumnWidth(column.evaluatedColumn.id)}
                        activeSessionRuns={activeSessionRuns}
                        errorRuns={errorRuns}
                        successfulRuns={successfulRuns}
                        currentSelection={currentSelection}
                        onCellSelection={onCellSelection}
                        onToggleLock={handleToggleEvalCellLock}
                        onRerunPending={markRerunPending}
                      />
                    );
                  } else if (column.attributeTestColumn) {
                    return (
                      <AttrTestColumnCell
                        key={column.attributeTestColumn.id}
                        procedureSessionId={auditSessionId}
                        drySourceRow={sourceRow}
                        column={column}
                        isHighlighted={isHighlighted}
                        highlightEdges={highlightEdges}
                        isLast={isLast}
                        width={getColumnWidth(column.attributeTestColumn.id)}
                        activeSessionRuns={activeSessionRuns}
                        errorRuns={errorRuns}
                        successfulRuns={successfulRuns}
                        currentSelection={currentSelection}
                        onCellSelection={onCellSelection}
                        onToggleLock={handleToggleAttrTestCellLock}
                        onRerunPending={markRerunPending}
                      />
                    );
                  } else if (column.analyticsColumn) {
                    return (
                      <AnalyticsColumnCell
                        key={column.analyticsColumn.id}
                        procedureSessionId={auditSessionId}
                        drySourceRow={sourceRow}
                        column={column}
                        isHighlighted={isHighlighted}
                        highlightEdges={highlightEdges}
                        isLast={isLast}
                        width={getColumnWidth(column.analyticsColumn.id)}
                        activeSessionRuns={activeSessionRuns}
                        errorRuns={errorRuns}
                        successfulRuns={successfulRuns}
                        currentSelection={currentSelection}
                        onCellSelection={onCellSelection}
                        onToggleLock={handleToggleAnalyticsCellLock}
                        onRerunPending={markRerunPending}
                      />
                    );
                  }
                  return reportUnrenderedColumnKind(column);
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        ref={bottomScrollRef}
        onScroll={syncScroll}
        className="sticky bottom-0 z-10 bg-white border-t border-stone-300 overflow-x-scroll"
      >
        <div style={{ width: totalContentWidth, height: 1 }} />
      </div>

      <RegenerateWithInstructionsDialog
        open={rowInstructionsDialogOpen}
        onOpenChange={setRowInstructionsDialogOpen}
        onSubmit={handleRegenerateSampleWithInstructions}
        title="Regenerate Sample with Instructions"
        description="Provide custom instructions to guide the regeneration of all columns for this sample."
      />

      <RegenerateWithInstructionsDialog
        open={columnInstructionsDialogOpen}
        onOpenChange={setColumnInstructionsDialogOpen}
        onSubmit={handleRerunColumnWithInstructions}
        title="Regenerate Column with Instructions"
        description="Provide custom instructions to guide the regeneration of this column across all samples."
      />
    </div>
  );
};
